// Translates rc_engine.py's match_reasons strings (backend data) into
// natural-language, patient-facing phrasing for the "Why We Matched You"
// checklist. Negative/internal signals (risk notes, "does not fully match")
// are filtered out entirely rather than phrased — the checklist only ever
// shows genuine positives, never a workaround wording of a downside.
const EXACT_PHRASING: Record<string, string> = {
  'Session style is an exact match': 'Matches your preferred session style',
  'Gender preference matched': 'Matches your therapist gender preference',
  'Cultural fit matched': 'Matches your cultural preference',
  'Accepts your insurance': 'Accepts your insurance',
  'Within your budget': 'Within your budget',
  'Virtual appointments available': 'Offers virtual appointments',
  'Matches age group': 'Sees your age group',
  'Licensed in your state': 'Licensed in your state',
  'Provider has availability within 7 days': 'Can see you within a week',
  'Accepting new patients': 'Accepting new patients',
}

const HIDDEN_EXACT = new Set([
  'Limited presenting-concern specialty overlap',
  'Modality preference does not fully match',
  'Risk increased: first appointment delayed more than 14 days',
  'Risk increased: logistical friction present',
  'Provider capacity is limited',
  // Redundant with "Accepting new patients", which already covers this.
  'Provider capacity is open',
  "Note: this provider's credentialing is still under review",
  'Broad fallback routing used: presenting concern not specified',
])

const PATTERNS: { regex: RegExp; render: (m: RegExpMatchArray) => string }[] = [
  { regex: /^Specialty fit: (.+)$/, render: (m) => `Specializes in ${m[1]}` },
  { regex: /^Modality match: (.+)$/, render: (m) => `Uses your preferred approach (${m[1]})` },
  { regex: /^Language match: (.+)$/, render: (m) => `Speaks ${m[1]}` },
  { regex: /^Accepts (.+)$/, render: (m) => `Accepts ${m[1]}` },
  { regex: /^(\d+) km away$/, render: (m) => `${m[1]} km away` },
]

/** Returns the natural-language phrasing for a single backend reason string,
 * or null if this reason shouldn't appear on the patient-facing checklist. */
export function phraseMatchReason(reason: string): string | null {
  if (HIDDEN_EXACT.has(reason)) return null
  if (reason in EXACT_PHRASING) return EXACT_PHRASING[reason]
  for (const { regex, render } of PATTERNS) {
    const m = reason.match(regex)
    if (m) return render(m)
  }
  // Unrecognized reasons (future signals not yet mapped here) are shown
  // as-is rather than silently dropped, so a new backend reason is never
  // invisible to patients while this map catches up.
  return reason
}

/** Filters + phrases a full match_reasons array for checklist rendering. */
export function phraseMatchReasons(reasons: string[]): string[] {
  return reasons.map(phraseMatchReason).filter((r): r is string => r !== null)
}

export const BROAD_MATCH_EXPLANATION = 'We expanded your search to find the best available match for you.'
